/**
 * AITutorLearning Component
 * Main learning interface for AI Tutor V2
 *
 * Features:
 * - HTML chapter rendering in iframe
 * - Collapsible chapter navigation sidebar
 * - Drag-resizable right chat panel
 * - Full narrator control bar (play/pause/stop, skip, volume, speed, progress)
 * - Voice doubt recording with AskDoubtButton
 * - RAG-powered Q&A chat
 * - CodeRunnerModal for code execution
 * - Video modal for embedded videos
 * - Selected text/image context panels
 * - Screenshot capture with paste support
 * - Keyboard shortcuts
 */

import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
  Chip,
  IconButton,
  Alert,
  Dialog,
  Tooltip,
  Slider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import {
  fetchSessionDetail,
  visitChapter,
  clearSessionState,
  setCurrentChapterIndex,
} from "./aiTutorSlice";
import AskDoubtButton from "./AskDoubtButton";
import CodeRunnerModal from "./CodeRunnerModal";
import { loadEngine, withEngine, createNarration, sectionForSelection } from "./narratorEngine";
import { getChapterHtml, getNarrationUrls, getTutorStatus, getVideos, tutorChatApi } from "./aiTutorApi";
import TutorTabs from "../../workspace/Tutor/TutorTabs";
import styles from "./AITutor.module.css";

// What the student is told when doubts cannot be answered right now.
const TUTOR_OFFLINE = {
  tutor_offline: "The tutor is offline right now. You can keep reading; questions will work again shortly.",
  models_offline: "The tutor is resting for a moment. Please try your question again in a few minutes.",
  lesson_not_on_tutor: "The tutor doesn't have this lesson yet. Your mentor has been asked to check it.",
  lesson_not_supported: "This lesson was uploaded before the tutor supported it. Ask your mentor to upload it again.",
};

const AITutorLearning = () => {
  const { id: courseId, sessionId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const iframeRef = useRef(null);
  // The shared chat's main conversation (workspace/Tutor/TutorChat): spoken
  // exchanges are appended to it, pasted images go to it, notes are read from it.
  const chatRef = useRef(null);
  const isDraggingRef = useRef(false);

  const {
    currentSession,
    sessionLoading,
    sessionError,
    currentChapterIndex,
    visitedChapters,
    doubtTurns,
    doubtLimit,
  } = useSelector((state) => state.aiTutor);

  // UI State
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [rightPaneCollapsed, setRightPaneCollapsed] = useState(false);
  const [rightPaneWidth, setRightPaneWidth] = useState(420);
  const [isResizing, setIsResizing] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // Narrator state
  const [narratorPlaying, setNarratorPlaying] = useState(false);
  const [narratorReady, setNarratorReady] = useState(false);
  const [narratorJunction, setNarratorJunction] = useState(false);
  const [narratorProgress, setNarratorProgress] = useState(0);
  const [narratorSection, setNarratorSection] = useState("");
  const [narratorSegmentInfo, setNarratorSegmentInfo] = useState({ index: 0, total: 0 });
  const [narratorSpeed, setNarratorSpeed] = useState(() => {
    const saved = localStorage.getItem("ai-tutor-narrator-speed");
    return saved ? parseFloat(saved) : 1;
  });
  const [narratorVolume, setNarratorVolume] = useState(() => {
    const saved = localStorage.getItem("ai-tutor-narrator-volume");
    return saved ? parseFloat(saved) : 1;
  });

  // Context state: the text selected in the chapter, the chat's quote.
  const [selectedText, setSelectedText] = useState("");

  // Video state
  const [showVideoSection, setShowVideoSection] = useState(false);
  const [videoModalUrl, setVideoModalUrl] = useState(null);
  const [videoModalTitle, setVideoModalTitle] = useState("");

  // Code runner state
  const [codeRunnerModal, setCodeRunnerModal] = useState(null);

  // Chapter HTML content (fetched and injected with narrator script)
  const [chapterHtmlContent, setChapterHtmlContent] = useState(null);
  const [chapterHtmlLoading, setChapterHtmlLoading] = useState(false);

  // Narration: text → wav through the backend's signed URLs (see narratorEngine.js)
  const narrationRef = useRef(null);
  // Read inside the message handler, which is registered once per settings change.
  const selectedTextRef = useRef("");
  const sectionRef = useRef("");
  const chapterRef = useRef(0);
  // What is on screen, from the chapter iframe: the sections in view (nearest
  // the middle first) and the one the current highlight is in.
  const visibleSectionsRef = useRef([]);
  const highlightSectionRef = useRef("");
  // The latest {text, section} from READER_REPORTS: it and the engine's
  // text_selected report the same selection, in either order.
  const reportedSectionRef = useRef(null);
  // Voice doubts are abandoned when the lesson is left — owned here, not by the
  // button, which unmounts whenever the right pane is collapsed.
  const voiceAbortRef = useRef(null);
  if (!narrationRef.current) narrationRef.current = createNarration(getNarrationUrls);

  // The mentor's uploaded videos, signed
  const [videos, setVideos] = useState([]);

  // Can doubts be answered right now? {online, reason}
  const [tutorStatus, setTutorStatus] = useState(null);

  // Send narrator command to iframe
  const sendNarrator = useCallback((type, value) => {
    iframeRef.current?.contentWindow?.postMessage({ type, value }, "*");
  }, []);

  // Fetch session details on mount
  useEffect(() => {
    if (sessionId) {
      dispatch(fetchSessionDetail(sessionId));
    }
    return () => {
      // Stop waiting for a spoken answer to a lesson that is being left (a
      // typed question is the chat's own: it is picked up again on return).
      voiceAbortRef.current?.abort();
      voiceAbortRef.current = null;
      dispatch(clearSessionState());
    };
  }, [dispatch, sessionId]);

  // Persist narrator settings
  useEffect(() => {
    localStorage.setItem("ai-tutor-narrator-speed", String(narratorSpeed));
  }, [narratorSpeed]);
  useEffect(() => {
    localStorage.setItem("ai-tutor-narrator-volume", String(narratorVolume));
  }, [narratorVolume]);

  // Reset narrator + context state when chapter changes
  useEffect(() => {
    setNarratorPlaying(false);
    setNarratorReady(false);
    setNarratorJunction(false);
    setNarratorProgress(0);
    setNarratorSection("");
    setNarratorSegmentInfo({ index: 0, total: 0 });
    setSelectedText("");
    setIframeLoaded(false);
    setChapterHtmlContent(null);
    selectedTextRef.current = "";
    sectionRef.current = "";
    visibleSectionsRef.current = [];
    highlightSectionRef.current = "";
    chapterRef.current = currentChapterIndex;
  }, [currentChapterIndex]);

  // Narration resolver, for the narrator inside the chapter iframe
  useEffect(() => {
    if (!sessionId) return undefined;
    const narration = narrationRef.current;
    narration.install(sessionId);
    return () => narration.uninstall();
  }, [sessionId]);

  // Uploaded videos (signed URLs, so they are fetched rather than built)
  useEffect(() => {
    if (!currentSession?.session_id) return;
    getVideos(currentSession.session_id).then(setVideos).catch(() => setVideos([]));
  }, [currentSession?.session_id]);

  // Tutor availability: checked now and every 30 s, so the chat can say the
  // tutor is offline before a student types a question.
  useEffect(() => {
    if (!sessionId) return undefined;
    let alive = true;
    const check = () =>
      getTutorStatus(sessionId)
        .then((s) => alive && setTutorStatus(s))
        .catch(() => alive && setTutorStatus({ online: false, reason: "tutor_offline" }));
    check();
    const iv = setInterval(check, 30000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [sessionId]);

  // Fetch the chapter (media already signed) and add the narrator engine
  //
  // Keyed on the session id and whether this chapter exists — not on the
  // session object, which is replaced after a voice doubt or a chapter visit.
  // Every fetch carries freshly signed URLs, so a needless refetch is a new
  // srcDoc: the iframe reloads and the student loses their place.
  const chapterSessionId = currentSession?.session_id;
  const chapterExists = Boolean(currentSession?.chapter_list?.[currentChapterIndex]);
  useEffect(() => {
    let alive = true;
    const fetchChapterHtml = async () => {
      if (!chapterSessionId || currentChapterIndex < 0 || !chapterExists) return;
      setChapterHtmlLoading(true);
      try {
        const [chapter, engine] = await Promise.all([
          getChapterHtml(chapterSessionId, currentChapterIndex),
          loadEngine(),
        ]);
        if (alive) setChapterHtmlContent(withEngine(chapter.html, engine));
      } catch (error) {
        console.error("Error loading chapter:", error);
        if (alive) setChapterHtmlContent(null);
      } finally {
        if (alive) setChapterHtmlLoading(false);
      }
    };
    fetchChapterHtml();
    return () => {
      alive = false;
    };
  }, [chapterSessionId, chapterExists, currentChapterIndex]);

  // Handle drag-resize right pane
  const handleMouseDown = useCallback((e) => {
    isDraggingRef.current = true;
    setIsResizing(true);
    e.preventDefault();
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      const newWidth = document.body.clientWidth - e.clientX;
      if (newWidth >= 300 && newWidth <= 800) {
        setRightPaneWidth(newWidth);
      }
    };
    const handleMouseUp = () => {
      isDraggingRef.current = false;
      setIsResizing(false);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // Receive events from narrator inside the iframe
  useEffect(() => {
    const handler = (e) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      const { type, progress, section } = e.data || {};
      if (type === "narrator:ready") {
        setNarratorReady(true);
        setNarratorSegmentInfo((prev) => ({ ...prev, total: e.data.total ?? 0 }));
        sendNarrator("narrator:speed", narratorSpeed);
        sendNarrator("narrator:volume", narratorVolume);
        // Every line this chapter will speak, so their audio is looked up in
        // one request before play is pressed.
        sendNarrator("narrator:queue");
      } else if (type === "narrator:queue_text") {
        narrationRef.current?.prefetch(e.data.texts || []);
      } else if (type === "narrator:playing") {
        setNarratorPlaying(true);
        setNarratorJunction(false);
      } else if (type === "narrator:paused") {
        setNarratorPlaying(false);
      } else if (type === "narrator:stopped") {
        setNarratorPlaying(false);
        setNarratorJunction(false);
        setNarratorProgress(0);
      } else if (type === "narrator:complete") {
        setNarratorPlaying(false);
        setNarratorJunction(false);
        setNarratorProgress(1);
      } else if (type === "narrator:segment") {
        setNarratorProgress(progress ?? 0);
        setNarratorSection(section ?? "");
        sectionRef.current = section ?? "";
        setNarratorSegmentInfo({ index: e.data.index ?? 0, total: e.data.total ?? 0 });
      } else if (type === "narrator:junction_pause") {
        setNarratorPlaying(false);
        setNarratorJunction(true);
      } else if (type === "viewport_sections") {
        visibleSectionsRef.current = Array.isArray(e.data.sections) ? e.data.sections.slice(0, 4) : [];
      } else if (type === "text_section") {
        reportedSectionRef.current = { text: e.data.text, section: e.data.section || "" };
        if (selectedTextRef.current && selectedTextRef.current === e.data.text) {
          highlightSectionRef.current = e.data.section || "";
        }
      } else if (type === "text_selected") {
        const text = e.data.text;
        highlightSectionRef.current = e.data.section || sectionForSelection(text, reportedSectionRef.current);
        setSelectedText(text);
        selectedTextRef.current = text;
      } else if (type === "text_deselected") {
        selectedTextRef.current = "";
        highlightSectionRef.current = "";
        setSelectedText("");
      } else if (type === "code:run") {
        setCodeRunnerModal({ code: e.data.code, language: e.data.language || "python" });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [sendNarrator, narratorSpeed, narratorVolume, sessionId]);

  // An image pasted anywhere on the page goes to the chat as an attachment
  // (a paste into the chat's own box is the chat's: it has handled it already).
  useEffect(() => {
    const handlePaste = (e) => {
      if (e.defaultPrevented) return;
      const files = [...(e.clipboardData?.files || [])].filter((f) => f.type.startsWith("image/"));
      if (!files.length) return;
      e.preventDefault();
      chatRef.current?.addImages(files);
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const chapterCount = currentSession?.chapter_list?.length || 0;
      switch (e.key) {
        case " ":
          e.preventDefault();
          if (narratorJunction) sendNarrator("narrator:resume");
          else narratorPlaying ? sendNarrator("narrator:pause") : sendNarrator("narrator:play");
          break;
        case "ArrowRight":
          if (currentChapterIndex < chapterCount - 1) {
            handleChapterSelect(currentChapterIndex + 1);
          }
          break;
        case "ArrowLeft":
          if (currentChapterIndex > 0) {
            handleChapterSelect(currentChapterIndex - 1);
          }
          break;
        case "Escape":
          if (videoModalUrl) setVideoModalUrl(null);
          if (codeRunnerModal) setCodeRunnerModal(null);
          break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [narratorPlaying, narratorJunction, currentChapterIndex, currentSession, videoModalUrl, codeRunnerModal, sendNarrator]);

  // Handle chapter navigation
  const handleChapterSelect = useCallback(
    (index) => {
      dispatch(setCurrentChapterIndex(index));
      dispatch(visitChapter({ sessionId, chapterIndex: index }));
      setIframeLoaded(false);
    },
    [dispatch, sessionId]
  );

  // Handle iframe load
  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
  }, []);

  // Handle chat message send
  // Read when a question is sent (typed or spoken), so it is what was on
  // screen at that moment. The highlight's section only while it is selected.
  const currentScreen = useCallback(
    () => ({
      visibleSections: visibleSectionsRef.current,
      highlightSection: selectedTextRef.current ? highlightSectionRef.current : "",
    }),
    []
  );

  // The chat (workspace/Tutor/TutorTabs) on this lesson: the main conversation
  // and the tabs branched from it. Each question carries where the student is.
  const chatApi = useMemo(() => tutorChatApi(sessionId), [sessionId]);
  const chatContext = useCallback(() => ({
    payload: {
      chapter_index: chapterRef.current,
      // Where the narrator says the student is: the tutor's best clue to "this".
      ...(sectionRef.current ? { section: sectionRef.current } : {}),
      ...(visibleSectionsRef.current.length ? { visible_sections: visibleSectionsRef.current } : {}),
      ...(selectedTextRef.current && highlightSectionRef.current ? { highlight_section: highlightSectionRef.current } : {}),
    },
    bubble: {},
  }), []);
  const quote = useMemo(() => (selectedText ? { text: selectedText, section: highlightSectionRef.current || "" } : null), [selectedText]);
  const clearQuote = useCallback(() => {
    setSelectedText("");
    selectedTextRef.current = "";
    highlightSectionRef.current = "";
  }, []);

  // Download chat notes
  const downloadChatNotes = useCallback(() => {
    const chapterTitle = currentSession?.chapter_list?.[currentChapterIndex]?.title || "Chapter";
    const chatContent = (chatRef.current?.getMessages() || [])
      .map((m) => {
        const voiceIcon = m.is_voice ? " (Voice)" : "";
        const roleLabel = m.role === "user" ? "You" : "AI Tutor";
        return `**${roleLabel}${voiceIcon}**: ${m.content}`;
      })
      .join("\n\n");
    const fullContent = `# Chat History: ${chapterTitle}\n\n${chatContent || "_No chat messages_"}`;
    const blob = new Blob([fullContent], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${chapterTitle.replace(/[^a-zA-Z0-9]/g, "_")}_notes.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [currentSession, currentChapterIndex]);

  // Pause narrator and resolve promise
  const pauseNarratorAsync = useCallback(() => {
    return new Promise((resolve) => {
      if (!narratorPlaying) {
        resolve();
        return;
      }
      let resolved = false;
      const done = () => {
        if (!resolved) {
          resolved = true;
          window.removeEventListener("message", handler);
          resolve();
        }
      };
      const handler = (e) => {
        if (e.data?.type === "narrator:paused" || e.data?.type === "narrator:stopped") done();
      };
      window.addEventListener("message", handler);
      sendNarrator("narrator:pause_after_current");
      setTimeout(done, 5000);
    });
  }, [narratorPlaying, sendNarrator]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate(`/dashboard/workspace/${courseId}`);
  }, [navigate, courseId]);

  // Loading state
  if (sessionLoading && !currentSession) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (sessionError) {
    return (
      <Box p={3}>
        <Alert severity="error">{String(sessionError)}</Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  // Extract session data
  const {
    package_title: packageTitle,
    chapter_list: chapterList = [],
    total_chapters: totalChapters = 0,
    progress_percentage: progress = 0,
    has_audio: hasAudio = false,
  } = currentSession || {};

  const currentChapter = chapterList[currentChapterIndex];

  // Videos for this chapter, then course-wide ones
  const chapterVideos = videos.filter((v) => v.chapter_id === currentChapter?.chunk_id);
  const globalVideos = videos.filter((v) => v.chapter_id == null);
  const visibleVideos = [...chapterVideos, ...globalVideos];

  return (
    <Box className={styles.learningContainer}>
      {/* Chapter Nav Sidebar */}
      <Box
        className={styles.chapterSidebarWrapper}
        sx={{
          width: sidebarCollapsed ? 40 : 240,
          transition: "width 0.2s ease",
        }}
      >
        <Box
          className={styles.sidebarHeader}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          sx={{ cursor: "pointer" }}
        >
          {!sidebarCollapsed && (
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              Chapters
            </Typography>
          )}
          <ChevronLeftIcon
            sx={{
              transform: sidebarCollapsed ? "rotate(180deg)" : "none",
              transition: "transform 0.2s",
            }}
          />
        </Box>
        {!sidebarCollapsed && (
          <Box className={styles.chapterList}>
            {chapterList.map((chapter, index) => (
              <Box
                key={chapter.chunk_id}
                className={`${styles.chapterItem} ${
                  index === currentChapterIndex ? styles.chapterItemActive : ""
                }`}
                onClick={() => handleChapterSelect(index)}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {visitedChapters.includes(index) ? (
                    <CheckCircleIcon sx={{ fontSize: 14, color: "#22c55e" }} />
                  ) : (
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        border: "2px solid #d1d5db",
                      }}
                    />
                  )}
                  <Typography variant="body2" noWrap>
                    {chapter.title}
                  </Typography>
                </Box>
                {chapter.page_number && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2.5 }}>
                    Page {chapter.page_number}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Main Content Area */}
      <Box className={styles.mainContentArea}>
        {/* Top Bar */}
        <Box className={styles.topBar}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {packageTitle || "AI Tutor"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Section {currentChapterIndex + 1} of {totalChapters}
                {currentChapter?.title && ` - ${currentChapter.title}`}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 100 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {Math.round(progress)}%
            </Typography>
          </Box>
        </Box>

        {/* Chapter Content with Narrator Controls */}
        <Box className={styles.chapterContentArea}>
          {/* Narrator Control Bar - Floating Overlay */}
          {currentChapter && hasAudio && (
            <Box className={styles.narratorControlBar}>
              {/* Volume */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <VolumeUpIcon sx={{ fontSize: 16, color: "#1e40af" }} />
                <Slider
                  value={narratorVolume}
                  onChange={(e, v) => {
                    setNarratorVolume(v);
                    sendNarrator("narrator:volume", v);
                  }}
                  min={0}
                  max={1}
                  step={0.05}
                  sx={{ width: 70, color: "#1e40af" }}
                  size="small"
                />
              </Box>

              {/* Transport controls */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => sendNarrator("narrator:prev")}
                  disabled={!narratorReady || narratorSegmentInfo.index === 0 || narratorJunction}
                  sx={{ visibility: narratorJunction ? "hidden" : "visible" }}
                >
                  <SkipPreviousIcon fontSize="small" />
                </IconButton>

                {narratorJunction ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => sendNarrator("narrator:resume")}
                    sx={{
                      bgcolor: "#f59e0b",
                      "&:hover": { bgcolor: "#d97706" },
                      minWidth: 90,
                      animation: "pulse 2s infinite",
                    }}
                  >
                    <PlayArrowIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Continue
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() =>
                      narratorPlaying ? sendNarrator("narrator:pause") : sendNarrator("narrator:play")
                    }
                    disabled={!narratorReady}
                    sx={{
                      bgcolor: "#1e40af",
                      "&:hover": { bgcolor: "#1e3a8a" },
                      minWidth: 90,
                    }}
                  >
                    {narratorPlaying ? (
                      <>
                        <PauseIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        Pause
                      </>
                    ) : (
                      <>
                        <PlayArrowIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        {narratorProgress > 0 ? "Resume" : "Play"}
                      </>
                    )}
                  </Button>
                )}

                <IconButton
                  size="small"
                  onClick={() => sendNarrator("narrator:next")}
                  disabled={
                    !narratorReady ||
                    narratorSegmentInfo.index >= Math.max(0, narratorSegmentInfo.total - 1) ||
                    narratorJunction
                  }
                  sx={{ visibility: narratorJunction ? "hidden" : "visible" }}
                >
                  <SkipNextIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Stop */}
              <IconButton
                size="small"
                onClick={() => sendNarrator("narrator:stop")}
                disabled={!narratorReady || (!narratorPlaying && !narratorJunction && narratorProgress === 0)}
              >
                <StopIcon fontSize="small" />
              </IconButton>

              {/* Progress */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: 140 }}>
                <LinearProgress
                  variant="determinate"
                  value={narratorProgress * 100}
                  sx={{ flex: 1, height: 4, borderRadius: 2 }}
                />
                <Typography variant="caption" sx={{ minWidth: 35, textAlign: "right" }}>
                  {narratorSegmentInfo.total > 0
                    ? `${narratorSegmentInfo.index + 1}/${narratorSegmentInfo.total}`
                    : ""}
                </Typography>
              </Box>

              {/* Speed */}
              <Button
                size="small"
                onClick={() => {
                  const speeds = [0.75, 1, 1.25, 1.5];
                  const nextIdx = (speeds.indexOf(narratorSpeed) + 1) % speeds.length;
                  const v = speeds[nextIdx];
                  setNarratorSpeed(v);
                  sendNarrator("narrator:speed", v);
                }}
                sx={{
                  minWidth: 48,
                  bgcolor: "#eef2ff",
                  color: "#1e40af",
                  "&:hover": { bgcolor: "#e0e7ff" },
                }}
              >
                {narratorSpeed}x
              </Button>

              {/* Status */}
              <Box sx={{ width: 150, display: "flex", alignItems: "center", gap: 0.5 }}>
                {narratorJunction ? (
                  <>
                    <AccessTimeIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
                    <Typography variant="caption" sx={{ color: "#d97706" }}>
                      Take a moment - click Continue
                    </Typography>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary" noWrap title={narratorSection}>
                    {narratorSection}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Iframe Container */}
          <Box className={styles.iframeContainer}>
            {isResizing && <Box className={styles.resizeOverlay} />}
            {(chapterHtmlLoading || (!iframeLoaded && chapterHtmlContent)) && (
              <Box className={styles.iframeLoader}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Loading chapter...
                </Typography>
              </Box>
            )}
            {chapterHtmlContent ? (
              <iframe
                key={currentChapterIndex}
                ref={iframeRef}
                srcDoc={chapterHtmlContent}
                className={styles.chapterIframe}
                title={currentChapter?.title || "Chapter Content"}
                onLoad={handleIframeLoad}
                sandbox="allow-scripts allow-same-origin"
              />
            ) : !chapterHtmlLoading ? (
              <Box className={styles.noChapter}>
                <Typography color="text.secondary">Select a chapter to begin learning</Typography>
              </Box>
            ) : null}
          </Box>

          {/* Navigation Bar */}
          <Box className={styles.navBar}>
            <Button
              variant="outlined"
              startIcon={<ChevronLeftIcon />}
              disabled={currentChapterIndex === 0}
              onClick={() => handleChapterSelect(currentChapterIndex - 1)}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              endIcon={<ChevronRightIcon />}
              disabled={currentChapterIndex >= totalChapters - 1}
              onClick={() => handleChapterSelect(currentChapterIndex + 1)}
              sx={{
                background: "linear-gradient(135deg, #2563eb, #1e40af)",
                "&:hover": { background: "linear-gradient(135deg, #1d4ed8, #1e3a8a)" },
              }}
            >
              Next Section
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Right Pane - Chat & Context. Always shown: whether doubts can be
          answered right now is the tutor-status banner's job. It used to be
          gated on has_rag_index, which is false for every lesson since the
          index moved to the tutor gateway — no student saw the chat. */}
      {true && (
        <Box
          className={styles.rightPane}
          sx={{
            width: rightPaneCollapsed ? 40 : rightPaneWidth,
            transition: rightPaneCollapsed ? "width 0.2s" : "none",
          }}
        >
          {/* Drag handle */}
          {!rightPaneCollapsed && (
            <Box className={styles.dragHandle} onMouseDown={handleMouseDown}>
              <Box className={styles.dragHandleInner} />
            </Box>
          )}

          {/* Collapse header */}
          <Box
            className={styles.rightPaneHeader}
            onClick={() => setRightPaneCollapsed(!rightPaneCollapsed)}
          >
            {!rightPaneCollapsed && (
              <Typography variant="subtitle2" fontWeight={600}>
                AI Tutor Chat
              </Typography>
            )}
            <ChevronRightIcon
              sx={{
                transform: rightPaneCollapsed ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          </Box>

          {!rightPaneCollapsed && (
            <Box className={styles.rightPaneContent}>
              {/* Voice Doubt Button */}
              {currentChapter && (
                <Box className={styles.voiceDoubtSection}>
                  <AskDoubtButton
                    getAbortSignal={() => {
                      if (!voiceAbortRef.current) voiceAbortRef.current = new AbortController();
                      return voiceAbortRef.current.signal;
                    }}
                    sessionId={sessionId}
                    chapterIndex={currentChapterIndex}
                    isNarratorPlaying={narratorPlaying}
                    onPauseNarrator={pauseNarratorAsync}
                    onMessageAdded={(msg) => {
                      chatRef.current?.append({ role: msg.role, content: msg.content, is_voice: !!msg.isVoice });
                    }}
                    selectedText={selectedText}
                    section={narratorSection}
                    getScreen={currentScreen}
                    doubtTurns={doubtTurns}
                    doubtLimit={doubtLimit}
                    onDoubtUsed={() => {
                      dispatch(fetchSessionDetail(sessionId));
                    }}
                  />
                </Box>
              )}

              {/* Video Resources */}
              {visibleVideos.length > 0 && (
                <Box className={styles.videoSection}>
                  <Box
                    className={styles.videoSectionHeader}
                    onClick={() => setShowVideoSection(!showVideoSection)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <VideoLibraryIcon sx={{ fontSize: 18, color: "#1e40af" }} />
                      <Typography variant="body2" fontWeight={500}>
                        Video Resources
                      </Typography>
                      <Chip label={visibleVideos.length} size="small" color="secondary" />
                    </Box>
                    <ChevronRightIcon
                      sx={{
                        transform: showVideoSection ? "rotate(90deg)" : "none",
                        transition: "transform 0.2s",
                      }}
                    />
                  </Box>
                  {showVideoSection && (
                    <Box className={styles.videoList}>
                      {visibleVideos.map((v) => (
                        <Box key={v.video_id} className={styles.videoItem}>
                          <VideoLibraryIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                          <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                            {v.title}
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                              setVideoModalUrl(v.url);
                              setVideoModalTitle(v.title);
                            }}
                            sx={{
                              bgcolor: "#1e40af",
                              "&:hover": { bgcolor: "#1e40af" },
                              minWidth: "auto",
                              px: 1.5,
                            }}
                          >
                            <PlayArrowIcon sx={{ fontSize: 14 }} />
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Can doubts be answered right now? Said before a student types one. */}
              {tutorStatus && !tutorStatus.online && (
                <Alert severity="info" sx={{ m: 1, flexShrink: 0 }}>
                  {TUTOR_OFFLINE[tutorStatus.reason] || TUTOR_OFFLINE.tutor_offline}
                </Alert>
              )}

              {/* The chat: the tutor chat shared with the reading and session
                  pages, in tabs. Text selected in the chapter is the quote;
                  words of an answer, or the quote, can open as a new tab. */}
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", backgroundColor: "#fff" }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", px: 1, flexShrink: 0,
                  borderBottom: "1px solid #eef0f2" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 1 }}>
                    {currentChapter?.title || ""}
                  </Typography>
                  <IconButton size="small" onClick={downloadChatNotes} title="Download chat notes" aria-label="Download chat notes">
                    <DownloadIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <TutorTabs api={chatApi} pendingId={`ai-tutor:${sessionId}`} context={chatContext} height="100%"
                    quote={quote} onClearQuote={clearQuote} mainRef={chatRef}
                    labels={{
                      rootTab: "Chat",
                      online: "Ask about this lesson",
                      offline: "The tutor is offline right now",
                      notReady: "The tutor doesn't have this lesson yet",
                      empty: "Ask anything about this chapter — or select text in the lesson to ask about it.",
                      footer: "Select text in the lesson, or in an answer, to ask about it in a new tab.",
                      clearBody: "The tutor starts fresh for this lesson, and its tabs close with it.",
                    }} />
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Video Modal */}
      <Dialog
        open={!!videoModalUrl}
        onClose={() => setVideoModalUrl(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: "black", borderRadius: 2 } }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1, bgcolor: "#111" }}>
          <Typography variant="subtitle2" sx={{ color: "white" }}>
            {videoModalTitle}
          </Typography>
          <IconButton size="small" onClick={() => setVideoModalUrl(null)} sx={{ color: "#9ca3af" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          component="video"
          src={videoModalUrl}
          controls
          autoPlay
          sx={{ width: "100%", maxHeight: "75vh" }}
        />
      </Dialog>

      {/* Code Runner Modal */}
      {codeRunnerModal && (
        <CodeRunnerModal
          code={codeRunnerModal.code}
          language={codeRunnerModal.language}
          onClose={() => setCodeRunnerModal(null)}
        />
      )}
    </Box>
  );
};

export default AITutorLearning;
